import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function BranchInventory() {
  const { bid } = useParams();
  const { staff } = useAuth();
  const canEdit = hasPermission(staff, "location.inventory.edit");
  const [branch, setBranch] = useState(null);
  const [rows, setRows] = useState([]);
  const [adj, setAdj] = useState(null);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const [b, r] = await Promise.all([
        staffApi.get("/branches"),
        staffApi.get(`/branches/${bid}/inventory`),
      ]);
      setBranch(b.data.find((x) => x.id === bid) || null);
      setRows(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  const applyAdj = async () => {
    if (!adj) return;
    const d = parseInt(delta, 10);
    if (Number.isNaN(d) || !reason) { toast.error("Provide delta and reason"); return; }
    try {
      await staffApi.post("/inventory/adjust", { branch_id: bid, product_id: adj.product_id, delta: d, reason });
      toast.success("Inventory updated");
      setAdj(null); setDelta(""); setReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <StaffShell branchName={branch?.name} context="branch">
      <h1 className="text-2xl font-semibold mb-6">Inventory</h1>
      <Card className="rounded-lg">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Reorder</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No inventory rows.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`inv-row-${r.product_id}`}>
                  <TableCell className="font-medium">{r.product?.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.product?.sku}</TableCell>
                  <TableCell>{formatBranchMoney(branch, r.product?.price)}</TableCell>
                  <TableCell>
                    {r.stock <= r.reorder_level ? (
                      <Badge className="bg-[hsl(var(--status-cancelled))] text-white rounded-full">{r.stock}</Badge>
                    ) : (
                      <span>{r.stock}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.reorder_level}</TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <Button size="sm" variant="outline" onClick={() => setAdj(r)} data-testid={`adj-btn-${r.product_id}`}>Adjust</Button>
                    ) : (
                      <span className="text-muted-foreground italic text-xs">Read-only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!adj} onOpenChange={(o) => !o && setAdj(null)}>
        <DialogContent data-testid="adj-modal">
          <DialogHeader>
            <DialogTitle>Adjust inventory · {adj?.product?.name}</DialogTitle>
            <DialogDescription>Record a stock adjustment. Use negative deltas for shrinkage.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Delta (+/- integer)</Label>
              <Input value={delta} onChange={(e) => setDelta(e.target.value)} data-testid="adj-delta" />
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adj-reason" />
            </div>
            <Button onClick={applyAdj} data-testid="adj-apply-btn">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
